import React, { useEffect, useRef, useState } from "react";
import {
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  addClient,
  editClient,
} from "../../../redux/reducers/ChildReducer/ClientReducer";
import { toast } from "react-toastify";
import { toggleRSBView } from "../../../redux/reducers/ChildReducer/RightSideBarReducer";
import { useFormik } from "formik";
import * as Yup from "yup";
import { setLoaderVisible } from "../../../redux/reducers/ChildReducer/LoaderReducer";
import { PostApi } from "../../../common/api/PostApi";
import { PutApi } from "../../../common/api/PutApi";
import { loadAnimation } from "lottie-web";
import { defineElement } from "lord-icon-element";
import Select from "react-select";
import { GetLocalStorageData } from "../../../meta/GetLocalStorageData";
import { SetLocalStorage } from "../../../meta/SetLocalStorageData";
import { timzone_options } from "../../../common/constant/Constants";
import CountryList from "../../../common/data/CountryList";
import LocationSearch from "../../../pages/organization/LocationSearch";
import no_image from "../../../assets/images/users/no_img.jpg";
import { EmployeeDisplayInSelect } from "../../../common/js/EmployeeDisplayInSelect";

defineElement(loadAnimation);

// ─── Initial location object ──────────────────────────────────────────────────
const initialLocationObj = {
  type_id: 0,
  type: "client",
  id: 0,
  address: "",
  city: "",
  pincode: "",
  country: "",
  state: "",
  timezone: "Asia/Kolkata",
  accurate_address: "",
  radius_range: 10,
  latitude: "8.3865046",
  longitude: "77.608336",
  location_id: "",
  name: "",
  image: null,
  image_url: null,
  map_data: [],
};

// ─── Custom Option renderer ───────────────────────────────────────────────────

const ClientForm = () => {
  const dispatch = useDispatch();
  const inputRef = useRef();
  const { form_obj } = useSelector((state) => state.RightSideBarReducer);

  const isEdit = !!(form_obj?.id && form_obj.id !== 0);

  const [clientData, setClientData] = useState({});

  // ─── Location modal state ─────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [locationObj, setLocationObj] = useState(initialLocationObj);
  const [locationStateOptions, setLocationStateOptions] = useState([]);
  const [CountryListOptions, setCountryListOptions] = useState([]);
  const [entityLocations, setEntityLocations] = useState([]);

  // ─── Build employee options ───────────────────────────────────────────────
  const EmployeeData = GetLocalStorageData("employee") || [];
  const employeeOptions = EmployeeData.map((emp) => ({
    ...emp,
    value: emp.id,
    label:
      `${emp.first_name || ""} ${emp.last_name || ""}`.trim() ||
      emp.name ||
      `Employee ${emp.id}`,
  }));

  // ─── Sync form_obj ────────────────────────────────────────────────────────
  useEffect(() => {
    if (form_obj) {
      setClientData(form_obj);
      // Load existing locations for this client if present
      setEntityLocations(form_obj?.entity_location_details || []);
    }
  }, [form_obj]);

  // ─── Country list ─────────────────────────────────────────────────────────
  useEffect(() => {
    const list = CountryList.filter((c) => c.show === true).map((d) => ({
      ...d,
      label: d.countryName,
      value: d.countryName,
    }));
    setCountryListOptions(list);
  }, []);

  // ─── State options when location country changes ──────────────────────────
  useEffect(() => {
    if (locationObj?.country) {
      const found = CountryList.find(
        (d) => d.countryName === locationObj.country,
      );
      setLocationStateOptions(found?.states || []);
    }
  }, [locationObj?.country]);

  // ─── Client input handler ─────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setClientData((prev) => ({ ...prev, [name]: value }));
  };

  // ─── Location helpers ─────────────────────────────────────────────────────
  const updateLocationObj = (e) => {
    const { name, value } = e.target;
    setLocationObj((prev) => ({ ...prev, [name]: value }));
  };

  const handleLocationCountryChange = (e) => {
    const found = CountryList.find((d) => d.countryName === e?.value);
    setLocationObj((prev) => ({ ...prev, country: e?.value || "", state: "" }));
    setLocationStateOptions(found?.states || []);
  };

  const handleLocationStateChange = (e) => {
    setLocationObj((prev) => ({ ...prev, state: e?.value || "" }));
  };

  const handleLocationImageUpdate = (e) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (readerEvent) => {
        setLocationObj((prev) => ({
          ...prev,
          image: e.target.files[0],
          image_url: readerEvent.target.result,
        }));
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleAddClick = () => {
    setLocationObj({
      ...initialLocationObj,
      type_id: form_obj?.id,
      type: "client",
    });
    setModalOpen(true);
  };

  const handleEditLocation = (location) => {
    setLocationObj({
      ...location,
      type_id: form_obj?.id,
      type: "client",
    });
    setModalOpen(true);
  };

  const handleModalClose = () => setModalOpen(false);

  // ─── Main client formik ───────────────────────────────────────────────────
  const FormikForm = useFormik({
    enableReinitialize: true,
    initialValues: clientData,
    validationSchema: Yup.object({
      name: Yup.string().required("Please Enter Client Name"),
      phone_number: Yup.string().required("Please Enter Phone Number"),
      city: Yup.string().required("Please Enter City"),
      state: Yup.string().required("Please Enter State"),
      country: Yup.string().required("Please Enter Country"),
      pincode: Yup.string().required("Please Enter Pincode"),
    }),
    onSubmit: async () => {
      dispatch(setLoaderVisible(true));
      try {
        const payload = {
          ...clientData,
          owner: clientData.owner || null,
          manager_id_list: clientData.manager_id_list || [],
        };
        delete payload._manager_raw;

        const Api = isEdit
          ? await PutApi(`clients/`, payload, false)
          : await PostApi("clients/", payload, false);

        if (Api?.status === 200 || Api?.status === 201) {
          const responseData = Api?.data?.data;
          dispatch(isEdit ? editClient(responseData) : addClient(responseData));

          const oldClients = GetLocalStorageData("clients") || [];
          const updatedClients = isEdit
            ? oldClients.map((item) =>
                item.id === responseData.id ? responseData : item,
              )
            : [...oldClients, responseData];
          SetLocalStorage("clients", updatedClients, 1);

          toast.success(Api?.data?.message || "Success");
          dispatch(toggleRSBView(false));
        } else {
          toast.error(Api?.data?.message || "Something went wrong");
        }
      } catch (error) {
        console.error(error);
        toast.error("API Error");
      }
      dispatch(setLoaderVisible(false));
    },
  });

  // ─── Location formik ──────────────────────────────────────────────────────
  const locationFormik = useFormik({
    enableReinitialize: true,
    initialValues: locationObj,
    validationSchema: Yup.object({
      name: Yup.string().required("Please Enter Location Name"),
      address: Yup.string().required("Please Enter Address"),
      country: Yup.string().required("Please Select Country"),
      state: Yup.string().required("Please Select State"),
      city: Yup.string().required("Please Enter City"),
      pincode: Yup.string().required("Please Enter Pincode"),
      timezone: Yup.string().required("Please Select Time Zone"),
      accurate_address: Yup.string().required("Please Enter Accurate Address"),
    }),
    onSubmit: async (values) => {
      dispatch(setLoaderVisible(true));
      try {
        const map_data_keys = [
          "address_components",
          "formatted_address",
          "geometry",
          "name",
          "place_id",
          "url",
          "utc_offset",
          "utc_offset_minutes",
          "vicinity",
        ];

        let map_data = [];
        if (
          typeof values.map_data === "object" &&
          values.map_data !== null &&
          !Array.isArray(values.map_data)
        ) {
          map_data = {};
          for (const key of map_data_keys) {
            if (Object.prototype.hasOwnProperty.call(values.map_data, key)) {
              map_data[key] = values.map_data[key];
            }
          }
        }

        const isNewLocation = values.id === 0;

        const payload = {
          type: "client",
          type_id: form_obj?.id,
          name: values.name,
          address: values.address,
          city: values.city,
          state: values.state,
          country: values.country,
          pincode: values.pincode,
          timezone: values.timezone,
          accurate_address: values.accurate_address,
          radius_range: values.radius_range,
          latitude: values.latitude,
          longitude: values.longitude,
          location_id: values.location_id || null,
          map_data: map_data,
        };

        const Api = isNewLocation
          ? await PostApi("location/entity-locations/", payload)
          : await PutApi(`location/entity-locations/${values.id}/`, payload);

        if ([200, 201].includes(Api?.status)) {
          const response = Api?.data;

          // Update local entity locations state
          setEntityLocations((prev) =>
            isNewLocation
              ? [...prev, response]
              : prev.map((loc) => (loc.id === response.id ? response : loc)),
          );

          toast.success(
            isNewLocation
              ? "Location added successfully."
              : "Location updated successfully.",
          );
          setModalOpen(false);
        } else {
          toast.error(
            Api?.data?.message ||
              Api?.data?.detail ||
              "Failed to save location.",
          );
        }
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.detail ||
            err?.message ||
            "API error occurred.",
        );
      } finally {
        dispatch(setLoaderVisible(false));
      }
    },
  });

  // ─── Focus first error field in location modal ────────────────────────────
  useEffect(() => {
    if (
      locationFormik.submitCount > 0 &&
      Object.keys(locationFormik.errors).length > 0
    ) {
      const firstErrorField = Object.keys(locationFormik.errors)[0];
      locationFormik.setFieldTouched(firstErrorField);
      const errorField = document.querySelector(`[name=${firstErrorField}]`);
      if (errorField) errorField.focus();
    }
  }, [locationFormik.submitCount, locationFormik.errors]);

  // ─── Derived select values ────────────────────────────────────────────────
  const ownerSelectValue =
    employeeOptions.find((e) => e.id === clientData.owner) || null;
  const managerSelectValue = employeeOptions.filter((e) =>
    (clientData.manager_id_list || []).includes(e.id),
  );

  const selectStyles = (touched, error) => ({
    control: (provided, state) => ({
      ...provided,
      borderColor:
        !state.isFocused && touched && error
          ? "red !important"
          : provided.borderColor,
    }),
  });

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <React.Fragment>
      <Row>
        <Col xl={40}>
          <Form onSubmit={FormikForm.handleSubmit}>
            <Row>
              {/* ── Client Name ── */}
              <Col md={12} lg={12} xxl={12}>
                <div className="mb-3">
                  <Label htmlFor="name" className="form-label">
                    Client Name *
                  </Label>
                  <div className="form-icon right">
                    <Input
                      type="text"
                      name="name"
                      id="name"
                      className="form-control"
                      placeholder="Client Name"
                      value={clientData.name || ""}
                      onChange={handleInputChange}
                      invalid={
                        FormikForm.touched.name && FormikForm.errors.name
                          ? true
                          : false
                      }
                    />
                    {!FormikForm.touched.name && !FormikForm.errors.name && (
                      <i className="ri-building-line" />
                    )}
                    {FormikForm.touched.name && FormikForm.errors.name ? (
                      <FormFeedback type="invalid">
                        {FormikForm.errors.name}
                      </FormFeedback>
                    ) : null}
                  </div>
                  {!FormikForm.errors.name && (
                    <div className="form-text">Enter Client Name.</div>
                  )}
                </div>
              </Col>

              {/* ── Owner ── */}
              <Col md={12} lg={12} xxl={12}>
                <div className="mb-3">
                  <Label htmlFor="owner" className="form-label">
                    Owner
                  </Label>
                  <Select
                    id="owner"
                    name="owner"
                    isClearable
                    isMulti={false}
                    options={employeeOptions}
                    components={{ Option: EmployeeDisplayInSelect }}
                    className="basic-single-select"
                    classNamePrefix="lc-select"
                    placeholder="Select Owner"
                    styles={selectStyles(
                      FormikForm.touched.owner,
                      FormikForm.errors.owner,
                    )}
                    value={ownerSelectValue}
                    onChange={(selectedOption) => {
                      setClientData((prev) => ({
                        ...prev,
                        owner: selectedOption ? selectedOption.id : null,
                      }));
                    }}
                  />
                  <div className="form-text">Select the client owner.</div>
                </div>
              </Col>

              {/* ── Managers ── */}
              <Col md={12} lg={12} xxl={12}>
                <div className="mb-3">
                  <Label htmlFor="manager_id_list" className="form-label">
                    Managers
                  </Label>
                  <Select
                    id="manager_id_list"
                    name="manager_id_list"
                    isClearable
                    isMulti={true}
                    options={employeeOptions}
                    components={{ Option: EmployeeDisplayInSelect }}
                    className="basic-multi-select"
                    classNamePrefix="lc-select"
                    placeholder="Select Managers"
                    styles={selectStyles(
                      FormikForm.touched.manager_id_list,
                      FormikForm.errors.manager_id_list,
                    )}
                    value={managerSelectValue}
                    onChange={(selectedOptions) => {
                      setClientData((prev) => ({
                        ...prev,
                        manager_id_list: selectedOptions
                          ? selectedOptions.map((o) => o.id)
                          : [],
                        _manager_raw: undefined,
                      }));
                    }}
                  />
                  <div className="form-text">Select one or more managers.</div>
                </div>
              </Col>

              {/* ── Address ── */}
              <Col md={12} lg={12} xxl={12}>
                <div className="mb-3">
                  <Label htmlFor="address" className="form-label">
                    Address
                  </Label>
                  <Input
                    type="textarea"
                    name="address"
                    id="address"
                    className="form-control"
                    placeholder="Address"
                    value={clientData.address || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </Col>

              {/* ── City / State ── */}
              <Col md={6} lg={6} xxl={6}>
                <div className="mb-3">
                  <Label htmlFor="city" className="form-label">
                    City *
                  </Label>
                  <Input
                    type="text"
                    name="city"
                    id="city"
                    className="form-control"
                    placeholder="City"
                    value={clientData.city || ""}
                    onChange={handleInputChange}
                    invalid={
                      FormikForm.touched.city && FormikForm.errors.city
                        ? true
                        : false
                    }
                  />
                  {FormikForm.touched.city && FormikForm.errors.city ? (
                    <FormFeedback type="invalid">
                      {FormikForm.errors.city}
                    </FormFeedback>
                  ) : null}
                </div>
              </Col>
              <Col md={6} lg={6} xxl={6}>
                <div className="mb-3">
                  <Label htmlFor="state" className="form-label">
                    State *
                  </Label>
                  <Input
                    type="text"
                    name="state"
                    id="state"
                    className="form-control"
                    placeholder="State"
                    value={clientData.state || ""}
                    onChange={handleInputChange}
                    invalid={
                      FormikForm.touched.state && FormikForm.errors.state
                        ? true
                        : false
                    }
                  />
                  {FormikForm.touched.state && FormikForm.errors.state ? (
                    <FormFeedback type="invalid">
                      {FormikForm.errors.state}
                    </FormFeedback>
                  ) : null}
                </div>
              </Col>

              {/* ── Pincode / Country ── */}
              <Col md={6} lg={6} xxl={6}>
                <div className="mb-3">
                  <Label htmlFor="pincode" className="form-label">
                    Pincode *
                  </Label>
                  <Input
                    type="text"
                    name="pincode"
                    id="pincode"
                    className="form-control"
                    placeholder="Pincode"
                    value={clientData.pincode || ""}
                    onChange={handleInputChange}
                    invalid={
                      FormikForm.touched.pincode && FormikForm.errors.pincode
                        ? true
                        : false
                    }
                  />
                  {FormikForm.touched.pincode && FormikForm.errors.pincode ? (
                    <FormFeedback type="invalid">
                      {FormikForm.errors.pincode}
                    </FormFeedback>
                  ) : null}
                </div>
              </Col>
              <Col md={6} lg={6} xxl={6}>
                <div className="mb-3">
                  <Label htmlFor="country" className="form-label">
                    Country *
                  </Label>
                  <Input
                    type="text"
                    name="country"
                    id="country"
                    className="form-control"
                    placeholder="Country"
                    value={clientData.country || ""}
                    onChange={handleInputChange}
                    invalid={
                      FormikForm.touched.country && FormikForm.errors.country
                        ? true
                        : false
                    }
                  />
                  {FormikForm.touched.country && FormikForm.errors.country ? (
                    <FormFeedback type="invalid">
                      {FormikForm.errors.country}
                    </FormFeedback>
                  ) : null}
                </div>
              </Col>

              {/* ── Country Code / Phone ── */}
              <Col md={6} lg={6} xxl={6}>
                <div className="mb-3">
                  <Label htmlFor="country_code" className="form-label">
                    Country Code
                  </Label>
                  <Input
                    type="text"
                    name="country_code"
                    id="country_code"
                    className="form-control"
                    placeholder="e.g. IN"
                    value={clientData.country_code || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </Col>
              <Col md={6} lg={6} xxl={6}>
                <div className="mb-3">
                  <Label htmlFor="phone_number" className="form-label">
                    Phone Number *
                  </Label>
                  <Input
                    type="text"
                    name="phone_number"
                    id="phone_number"
                    className="form-control"
                    placeholder="+91 9876543210"
                    value={clientData.phone_number || ""}
                    onChange={handleInputChange}
                    invalid={
                      FormikForm.touched.phone_number &&
                      FormikForm.errors.phone_number
                        ? true
                        : false
                    }
                  />
                  {FormikForm.touched.phone_number &&
                  FormikForm.errors.phone_number ? (
                    <FormFeedback type="invalid">
                      {FormikForm.errors.phone_number}
                    </FormFeedback>
                  ) : null}
                </div>
              </Col>

              {/* ── Website ── */}
              <Col md={12} lg={12} xxl={12}>
                <div className="mb-3">
                  <Label htmlFor="website" className="form-label">
                    Website
                  </Label>
                  <Input
                    type="text"
                    name="website"
                    id="website"
                    className="form-control"
                    placeholder="https://example.com"
                    value={clientData.website || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </Col>

              {/* ── VAT / GST ── */}
              <Col md={6} lg={6} xxl={6}>
                <div className="mb-3">
                  <Label htmlFor="vat_number" className="form-label">
                    VAT Number
                  </Label>
                  <Input
                    type="text"
                    name="vat_number"
                    id="vat_number"
                    className="form-control"
                    placeholder="VAT Number"
                    value={clientData.vat_number || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </Col>
              <Col md={6} lg={6} xxl={6}>
                <div className="mb-3">
                  <Label htmlFor="gst_number" className="form-label">
                    GST Number
                  </Label>
                  <Input
                    type="text"
                    name="gst_number"
                    id="gst_number"
                    className="form-control"
                    placeholder="GST Number"
                    value={clientData.gst_number || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </Col>

              {/* ── Locations Table (edit mode only) ── */}
              {isEdit && entityLocations.length > 0 && (
                <Col lg={12}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Address</th>
                        <th>Radius</th>
                        <th>Image</th>
                        <th>Edit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entityLocations.map((location, index) => (
                        <tr key={index}>
                          <td>{location.name}</td>
                          <td>{location.accurate_address}</td>
                          <td>{location.radius_range} m</td>
                          <td>
                            <img
                              src={
                                location.image_url
                                  ? location.image_url
                                  : no_image
                              }
                              alt="location"
                              style={{ height: "50px", objectFit: "cover" }}
                            />
                          </td>
                          <td>
                            <a
                              onClick={() => handleEditLocation(location)}
                              className="text-primary d-inline-block edit-item-btn ms-2"
                              href="javascript:void(0)"
                            >
                              <i className="ri-pencil-fill fs-16"></i>
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Col>
              )}

              {/* ── Bottom row: Add Location + Save ── */}
              <Col lg={12}>
                <Row className="mt-2">
                  <Col lg={6} className="text-start">
                    {isEdit && (
                      <Button
                        color="primary"
                        type="button"
                        onClick={handleAddClick}
                      >
                        Add Location <i className="ri-map-pin-line ms-1"></i>
                      </Button>
                    )}
                  </Col>
                  <Col lg={6} className="text-end">
                    <Button color="success" className="btn-label" type="submit">
                      <i className="ri-check-double-line label-icon align-middle fs-16 me-2"></i>
                      {isEdit ? "Update" : "Save"}
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>

      {/* ── Location Modal ── */}
      <Modal isOpen={modalOpen} toggle={handleModalClose} size="md">
        <ModalHeader toggle={handleModalClose}>
          {locationObj.id === 0 ? "Add New Location" : "Edit Location"}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={locationFormik.handleSubmit}>
            <Row className="gy-4">
              {/* Location Name */}
              <Col lg={12}>
                <Label className="form-label">Location Name</Label>
                <div className="form-icon right">
                  <Input
                    type="text"
                    name="name"
                    innerRef={inputRef}
                    placeholder="Enter Location Name"
                    value={locationObj.name || ""}
                    onChange={updateLocationObj}
                    invalid={
                      locationFormik.touched.name &&
                      !!locationFormik.errors.name
                    }
                  />
                  {!locationFormik.touched.name &&
                    !locationFormik.errors.name && (
                      <i className="ri-building-2-line"></i>
                    )}
                  {locationFormik.touched.name &&
                    locationFormik.errors.name && (
                      <FormFeedback>{locationFormik.errors.name}</FormFeedback>
                    )}
                </div>
              </Col>

              {/* Map / Accurate Address Search */}
              <LocationSearch
                locationObj={locationObj}
                setLocationObj={setLocationObj}
                LocationFormikForm={locationFormik}
              />

              {/* Address */}
              <Col lg={12}>
                <Label className="form-label">Address</Label>
                <Input
                  type="textarea"
                  name="address"
                  rows="2"
                  value={locationObj.address || ""}
                  onChange={updateLocationObj}
                  invalid={
                    locationFormik.touched.address &&
                    !!locationFormik.errors.address
                  }
                />
                {locationFormik.touched.address &&
                  locationFormik.errors.address && (
                    <FormFeedback>{locationFormik.errors.address}</FormFeedback>
                  )}
              </Col>

              {/* Country */}
              <Col lg={12}>
                <Label className="form-label">Country</Label>
                <Select
                  options={CountryListOptions}
                  value={
                    CountryListOptions.find(
                      (d) => d.value === locationObj?.country,
                    ) || null
                  }
                  onChange={handleLocationCountryChange}
                />
                {locationFormik.touched.country &&
                  locationFormik.errors.country && (
                    <div className="lc_form_error_text">
                      {locationFormik.errors.country}
                    </div>
                  )}
              </Col>

              {/* State */}
              <Col lg={12}>
                <Label className="form-label">State</Label>
                <Select
                  options={locationStateOptions}
                  value={
                    locationStateOptions.find(
                      (d) => d.value === locationObj?.state,
                    ) || null
                  }
                  onChange={handleLocationStateChange}
                />
                {locationFormik.touched.state &&
                  locationFormik.errors.state && (
                    <div className="lc_form_error_text">
                      {locationFormik.errors.state}
                    </div>
                  )}
              </Col>

              {/* City */}
              <Col lg={12}>
                <Label className="form-label">City</Label>
                <div className="form-icon right">
                  <Input
                    type="text"
                    name="city"
                    placeholder="City"
                    value={locationObj.city || ""}
                    onChange={updateLocationObj}
                    invalid={
                      locationFormik.touched.city &&
                      !!locationFormik.errors.city
                    }
                  />
                  {!locationFormik.touched.city &&
                    !locationFormik.errors.city && (
                      <i className="ri-map-pin-line"></i>
                    )}
                  {locationFormik.touched.city &&
                    locationFormik.errors.city && (
                      <FormFeedback>{locationFormik.errors.city}</FormFeedback>
                    )}
                </div>
              </Col>

              {/* Pincode */}
              <Col lg={12}>
                <Label className="form-label">Pincode</Label>
                <div className="form-icon right">
                  <Input
                    type="number"
                    name="pincode"
                    placeholder="600001"
                    value={locationObj.pincode || ""}
                    onChange={updateLocationObj}
                    invalid={
                      locationFormik.touched.pincode &&
                      !!locationFormik.errors.pincode
                    }
                  />
                  {!locationFormik.touched.pincode &&
                    !locationFormik.errors.pincode && (
                      <i className="ri-mail-line"></i>
                    )}
                  {locationFormik.touched.pincode &&
                    locationFormik.errors.pincode && (
                      <FormFeedback>
                        {locationFormik.errors.pincode}
                      </FormFeedback>
                    )}
                </div>
              </Col>

              {/* Image */}
              <Col lg={12}>
                <Label className="form-label">Image</Label>
                <Input
                  type="file"
                  name="image"
                  id="image"
                  className="form-control"
                  onChange={handleLocationImageUpdate}
                />
                {locationObj.image_url && (
                  <img
                    src={locationObj.image_url}
                    alt="preview"
                    className="mt-2"
                    style={{
                      height: "60px",
                      objectFit: "cover",
                      borderRadius: "4px",
                    }}
                  />
                )}
              </Col>

              {/* Timezone */}
              <Col lg={12}>
                <Label className="form-label">Time Zone</Label>
                <Select
                  name="timezone"
                  value={
                    locationObj.timezone
                      ? timzone_options.find(
                          (e) => e.value === locationObj.timezone,
                        )
                      : null
                  }
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      borderColor:
                        !state.isFocused &&
                        locationFormik.touched.timezone &&
                        locationFormik.errors.timezone
                          ? "red"
                          : provided.borderColor,
                    }),
                  }}
                  onChange={(e) =>
                    setLocationObj((prev) => ({ ...prev, timezone: e.value }))
                  }
                  options={timzone_options}
                  placeholder="Select a time zone"
                />
                {locationFormik.touched.timezone &&
                  locationFormik.errors.timezone && (
                    <div className="lc_form_error_text">
                      {locationFormik.errors.timezone}
                    </div>
                  )}
              </Col>

              {/* Modal Save */}
              <Col lg={12} className="text-end">
                <Button color="success" type="submit" className="btn-label">
                  <i className="ri-check-double-line label-icon align-middle fs-16 me-2"></i>
                  Save Location
                </Button>
              </Col>
            </Row>
          </Form>
        </ModalBody>
        <ModalFooter />
      </Modal>
    </React.Fragment>
  );
};

export default ClientForm;
